import { useCallback, useMemo } from 'react';
import { useAuth } from 'react-oidc-context';
import { useAppDispatch, useAppStore } from '../../redux/app/hooks';
import { setUserPreferences } from '../../redux/features/app/appSlice';
import { PreferencesService } from '../services/PreferencesService';
import { ResponseStyle, UserPreferences } from '../models/UserPreferences';

export const useUserPreferences = () => {
    const auth = useAuth();
    const dispatch = useAppDispatch();
    // Mutations read their baseline and revert target from the shared store when they run, not from a
    // per-instance snapshot: SettingsView and ResponseStyleSwitcher only ever update, never fetch, so
    // their own snapshot would never leave the default.
    const store = useAppStore();
    const token = auth.user?.access_token;

    const preferencesService = useMemo(() => new PreferencesService(token ?? ''), [token]);

    const fetchPreferences = useCallback(async (): Promise<UserPreferences | undefined> => {
        try {
            const prefs = await preferencesService.getPreferencesAsync();
            dispatch(setUserPreferences(prefs));
            return prefs;
        } catch (error) {
            console.error('Error fetching user preferences:', error);
            return undefined;
        }
    }, [preferencesService, dispatch]);

    const updateResponseStyle = useCallback(
        async (style: ResponseStyle): Promise<void> => {
            const previous = store.getState().app.userPreferences;
            // Optimistically clear nudge state too — backend will reset counters
            // on a style change, so the popover should not reappear on the next
            // GET. The actual server values overwrite this once the PUT returns.
            const next: UserPreferences = {
                responseStyle: style,
                shouldShowResponseStyleNudge: false,
                suggestedStyle: undefined,
            };
            dispatch(setUserPreferences(next));
            try {
                const confirmed = await preferencesService.updatePreferencesAsync({ responseStyle: style });
                dispatch(setUserPreferences(confirmed));
            } catch (error) {
                console.error('Error updating user preferences, reverting:', error);
                dispatch(setUserPreferences(previous));
                throw error;
            }
        },
        [preferencesService, dispatch, store],
    );

    const dismissRestyleNudge = useCallback(async (): Promise<void> => {
        const previous = store.getState().app.userPreferences;
        const next: UserPreferences = {
            ...previous,
            shouldShowResponseStyleNudge: false,
            suggestedStyle: undefined,
        };
        dispatch(setUserPreferences(next));
        try {
            const confirmed = await preferencesService.dismissRestyleNudgeAsync();
            dispatch(setUserPreferences(confirmed));
        } catch (error) {
            console.error('Error dismissing response-style nudge, reverting:', error);
            dispatch(setUserPreferences(previous));
            throw error;
        }
    }, [preferencesService, dispatch, store]);

    return {
        fetchPreferences,
        updateResponseStyle,
        dismissRestyleNudge,
    };
};
